import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { Film, Scissors, Flame, Upload, ExternalLink } from "lucide-react";


const KINDS = [
  { key: "vod", label: "VOD", Icon: Film, color: "#00F0FF" },
  { key: "clip", label: "Clip", Icon: Scissors, color: "#B084FF" },
  { key: "highlight", label: "Highlight", Icon: Flame, color: "#FF007F" },
];


export default function Vault() {
  const { user } = useAuth();
  const [links, setLinks] = useState([]);
  const [fixtures, setFixtures] = useState([]);
  const [teams, setTeams] = useState([]);
  const [kindFilter, setKindFilter] = useState("");
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    fixture_id: "",
    url: "",
    title: "",
    kind: "vod",
    player_handles: "",
  });

  const load = async () => {
    try {
      const [l, f, t] = await Promise.all([
        api.get("/game-links", { params: kindFilter ? { kind: kindFilter } : {} }),
        api.get("/fixtures"),
        api.get("/teams"),
      ]);
      setLinks(l.data || []);
      setFixtures((f.data || []).sort((a, b) => (b.scheduled_at || "").localeCompare(a.scheduled_at || "")));
      setTeams(t.data || []);
    } catch (e) { /* silent */ }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [kindFilter]);

  const teamMap = useMemo(() => Object.fromEntries(teams.map((t) => [t.id, t])), [teams]);
  const fxMap = useMemo(() => Object.fromEntries(fixtures.map((f) => [f.id, f])), [fixtures]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.fixture_id) { toast.error("Pick a fixture."); return; }
    if (!/^https?:\/\//i.test(form.url.trim())) { toast.error("URL must start with http(s)://"); return; }
    setBusy(true);
    try {
      await api.post(`/fixtures/${form.fixture_id}/game-links`, {
        url: form.url.trim(),
        title: form.title.trim() || null,
        kind: form.kind,
        player_handles: form.player_handles.split(",").map((s) => s.trim()).filter(Boolean),
      });
      toast.success("Game link added.");
      setForm({ fixture_id: "", url: "", title: "", kind: "vod", player_handles: "" });
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Add failed");
    } finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Remove this game link?")) return;
    try {
      await api.delete(`/game-links/${id}`);
      toast.success("Removed.");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Remove failed");
    }
  };

  const canUpload = !!user; // backend will enforce admin/rep gate; we show form to any logged-in user and surface the 403 cleanly.

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-8">
      <header>
        <div className="text-[10px] font-mono uppercase tracking-widest text-pink-400 mb-2">/ Film Room</div>
        <h1 className="font-heading font-black text-5xl tracking-tighter uppercase">Vault</h1>
        <p className="text-gray-400 mt-2 max-w-2xl">
          Every VOD, clip, and highlight pinned to a match or a player — the season's film archive. Reps + admins add links; everyone can watch.
        </p>
      </header>

      {canUpload && (
        <form
          onSubmit={submit}
          data-testid="vault-upload-form"
          className="border border-[#232330] bg-[#101015] p-5 space-y-3"
        >
          <div className="text-[10px] font-mono uppercase tracking-widest text-cyan-400 flex items-center gap-2">
            <Upload className="w-3.5 h-3.5" /> Submit a game link
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <select
              data-testid="vault-fixture-select"
              value={form.fixture_id}
              onChange={(e) => setForm({ ...form, fixture_id: e.target.value })}
              className="h-10 bg-[#05050A] border border-[#232330] px-3 text-sm text-white font-mono uppercase tracking-widest"
            >
              <option value="">— Choose fixture —</option>
              {fixtures.slice(0, 200).map((f) => {
                const h = teamMap[f.home_team_id]?.tag || "TBD";
                const a = teamMap[f.away_team_id]?.tag || "TBD";
                const d = f.scheduled_at ? new Date(f.scheduled_at).toLocaleDateString() : "unscheduled";
                return (
                  <option key={f.id} value={f.id}>
                    {h} vs {a} · {d}
                  </option>
                );
              })}
            </select>
            <select
              data-testid="vault-kind-select"
              value={form.kind}
              onChange={(e) => setForm({ ...form, kind: e.target.value })}
              className="h-10 bg-[#05050A] border border-[#232330] px-3 text-sm text-white font-mono uppercase tracking-widest"
            >
              {KINDS.map((k) => <option key={k.key} value={k.key}>{k.label}</option>)}
            </select>
          </div>
          <Input
            data-testid="vault-url"
            placeholder="https://twitch.tv/videos/... or youtu.be/..."
            value={form.url}
            onChange={(e) => setForm({ ...form, url: e.target.value })}
            className="rounded-none bg-[#05050A] border-[#232330] text-sm"
          />
          <Input
            data-testid="vault-title"
            placeholder="Short title (optional)"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            className="rounded-none bg-[#05050A] border-[#232330] text-sm"
          />
          <Input
            data-testid="vault-handles"
            placeholder="Tag players (comma-separated handles — optional)"
            value={form.player_handles}
            onChange={(e) => setForm({ ...form, player_handles: e.target.value })}
            className="rounded-none bg-[#05050A] border-[#232330] text-sm"
          />
          <Button
            type="submit"
            disabled={busy}
            data-testid="vault-submit"
            className="rounded-none bg-[#FF007F] hover:bg-pink-400 text-white uppercase tracking-widest text-xs font-bold"
          >
            {busy ? "Submitting…" : "Add to vault"}
          </Button>
        </form>
      )}

      <div className="flex gap-2 flex-wrap">
        <button
          onClick={() => setKindFilter("")}
          data-testid="vault-filter-all"
          className={`px-3 h-8 text-[11px] font-mono uppercase tracking-widest border ${kindFilter === "" ? "border-white text-white" : "border-[#232330] text-gray-500 hover:text-white"}`}
        >
          All ({links.length})
        </button>
        {KINDS.map((k) => (
          <button
            key={k.key}
            onClick={() => setKindFilter(k.key)}
            data-testid={`vault-filter-${k.key}`}
            className={`px-3 h-8 text-[11px] font-mono uppercase tracking-widest border inline-flex items-center gap-1.5 ${kindFilter === k.key ? "text-black" : "text-gray-400 hover:text-white"}`}
            style={{
              borderColor: kindFilter === k.key ? k.color : "#232330",
              background: kindFilter === k.key ? k.color : "transparent",
            }}
          >
            <k.Icon className="w-3 h-3" /> {k.label}
          </button>
        ))}
      </div>

      <div className="space-y-2" data-testid="vault-list">
        {links.length === 0 && (
          <div className="border border-[#232330] bg-[#101015] p-12 text-center text-gray-500 text-xs uppercase font-mono tracking-widest">
            No tapes yet — the vault is empty.
          </div>
        )}
        {links.map((l) => {
          const fx = fxMap[l.fixture_id];
          const h = teamMap[l.home_team_id]?.tag || "TBD";
          const a = teamMap[l.away_team_id]?.tag || "TBD";
          const kind = KINDS.find((k) => k.key === l.kind) || KINDS[0];
          const KindIcon = kind.Icon;
          const mine = user && (user.role === "admin" || user.role === "official" || l.uploaded_by === user.id);
          return (
            <div
              key={l.id}
              data-testid={`vault-row-${l.id}`}
              className="border border-[#232330] bg-[#101015] p-4 flex items-center gap-4 hover:border-[#333]"
            >
              <div
                className="h-10 w-10 shrink-0 flex items-center justify-center border"
                style={{ color: kind.color, borderColor: kind.color }}
              >
                <KindIcon className="w-5 h-5" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-gray-500">
                  <span style={{ color: kind.color }}>{kind.label}</span>
                  <span>·</span>
                  <span>{l.source}</span>
                  <span>·</span>
                  <span className="text-white">{h} vs {a}</span>
                  {l.round && (<><span>·</span><span>R{l.round}</span></>)}
                  <span>·</span>
                  <span>{new Date(l.uploaded_at).toLocaleDateString()}</span>
                </div>
                <div className="text-sm text-white mt-1 truncate">
                  {l.title || l.url}
                </div>
                {(l.player_handles || []).length > 0 && (
                  <div className="mt-1 flex flex-wrap gap-1.5">
                    {l.player_handles.slice(0, 8).map((h) => (
                      <Link
                        key={h}
                        to={`/player/${encodeURIComponent(h)}`}
                        className="text-[10px] font-mono uppercase tracking-widest text-cyan-400 hover:text-white border border-[#232330] px-1.5 py-0.5"
                      >
                        {h}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <a
                  href={l.url}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`vault-open-${l.id}`}
                  className="text-[10px] font-mono uppercase tracking-widest text-cyan-400 hover:text-white border border-cyan-400 hover:border-white px-2.5 py-1.5 inline-flex items-center gap-1"
                >
                  <ExternalLink className="w-3 h-3" /> Watch
                </a>
                {mine && (
                  <button
                    onClick={() => del(l.id)}
                    data-testid={`vault-delete-${l.id}`}
                    className="text-[10px] font-mono uppercase tracking-widest text-gray-500 hover:text-red-400 border border-[#232330] hover:border-red-400 px-2.5 py-1.5"
                  >
                    Remove
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
